import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators, FormGroup, FormArray, FormBuilder } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { Option } from 'src/app/constants/option';
import { GeneralOption } from 'src/app/models/general-option.t';
import { Role } from 'src/app/models/role';
import { User } from 'src/app/models/user';
import { UserRoles } from 'src/app/models/user-roles';
import { ApplicationService } from 'src/app/services/application.service';
import { UserService } from 'src/app/services/user/user.service';
import { TokenStorageService } from '../../../services/token-storage/token-storage.service';

@Component({
  selector: 'app-user-dialog',
  templateUrl: './user-dialog.component.html',
  styleUrls: ['./user-dialog.component.css']
})
export class UserDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  isNew = !this.data;
  roles: Role[] = [];
  isLoading = false;
  email = new FormControl({ value: '', disabled: !this.isNew }, [
    Validators.required,
  ]);
  name = new FormControl('', [Validators.required]);
  password = new FormControl('', [Validators.required]);
  retypePassword = new FormControl('', [Validators.required]);
  substansi = new FormControl(Option.default.id, [Validators.required]);
  atasan = new FormControl('', [Validators.required]);
  pejabat = new FormControl('', [Validators.required]);
  filteredAtasans: User[] = [];
  filteredPejabats: User[] = [];

  hidePassword = true;
  form: any = {
    username: null,
    password: null,
    retypePassword: null,
    name: null,
    roles: null,
  };

  formRole: FormGroup;

  onDestroy$ = new Subject<void>();

  substansis: GeneralOption[] = [];

  get rolesFormArray() {
    return this.formRole.controls['roles'] as FormArray;
  }

  constructor(
    public matDialogRef: MatDialogRef<UserDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: User,
    private applicationService: ApplicationService,
    private userService: UserService,
    private snackBar: MatSnackBar,
    private formBuilder: FormBuilder,
    private tokenStorageService: TokenStorageService,
  ) {
    this.formRole = this.formBuilder.group({
      roles: new FormArray([]),
    });
  }

  ngAfterViewInit(): void {
    if (!this.isNew) {
      this.email.setValue(this.data.username);
      this.name.setValue(this.data.fullname);
      this.atasan.setValue({ id: this.data.atasanId, name: this.data.atasanName });
      this.pejabat.setValue({ id: this.data.pejabatCutiId, name: this.data.pejabatName });
      this.substansi.setValue(this.data.substansiId || Option.default.id)
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  ngOnInit(): void {
    this.applicationService.data$.pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        this.substansis = data.substansis;
      });
    this.userService.getRoles().subscribe((roleList) => {
      this.roles = roleList.roles;
      this.roles.forEach((role) => {
        this.rolesFormArray.push(
          new FormControl(
            !this.isNew &&
            this.data.userRoles.some(
              (userRole) => userRole.roleId === role.id
            )
          )
        );
      });
    });

    this.atasan.valueChanges
      .pipe(debounceTime(200), takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (value && value !== '' && !value.fullname) {
          this.userService
            .getUsers({ dataPerPage: 10, page: 0, searchTerm: value })
            .subscribe((data) => {
              this.filteredAtasans = data.users;

              if (!this.isNew && this.data.atasanId) {
                if (this.data.atasanId === value.id) {
                  const item = this.filteredAtasans.find(
                    (item) => item.id === this.data.atasanId
                  );
                  if (item) {
                    this.atasan.setValue(item);
                  } else {
                    this.atasan.setValue({ id: this.data.atasanId, fullname: this.data.atasanName });
                  }
                }
              }
            });
        }
      });

    this.pejabat.valueChanges
      .pipe(debounceTime(200), takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (value && value !== '' && !value.fullname) {
          this.userService
            .getUsers({ dataPerPage: 10, page: 0, searchTerm: value })
            .subscribe((data) => {
              this.filteredPejabats = data.users;

              if (!this.isNew && this.data.pejabatCutiId) {
                if (this.data.pejabatCutiId === value.id) {
                  const item = this.filteredPejabats.find(
                    (item) => item.id === this.data.pejabatCutiId
                  );
                  if (item) {
                    this.pejabat.setValue(item);
                  } else {
                    this.pejabat.setValue({ id: this.data.pejabatCutiId, fullname: this.data.pejabatName });
                  }
                }
              }
            });
        }
      });
  }

  displayOpt(data: User): string {
    return data ? `${data.fullname}` : '';
  }

  isSuperAdmin() {
    return this.tokenStorageService.isSuperadmin();
  }

  onSubmit(): void {
    this.isLoading = true;
    const selectedRoleIds: number[] = this.formRole.value.roles
      .map((checked: any, i: number) => (checked ? this.roles[i].id : null))
      .filter((v: null) => v !== null);
    const userRoleList: UserRoles[] = selectedRoleIds.map(
      (roleId: number) =>
        <UserRoles>{
          roleId: roleId,
        }
    );
    if (this.isNew) {
      const user: User = {
        fullname: this.name.value,
        username: this.email.value,
        userRoles: userRoleList,
        rawPassword: this.password.value,
        atasanId: this.atasan.value.id,
        pejabatCutiId: this.pejabat.value.id,
        substansiId: this.substansi.value,
        enabled: 1,
      };
      this.userService.save(user).subscribe((user) => {
        this.showSnackBar('User berhasil ditambahkan');
        this.matDialogRef.close(true);
        this.isLoading = false;
      });
    } else {
      const user: User = {
        id: this.data.id,
        fullname: this.name.value,
        username: this.data.username,
        userRoles: userRoleList,
        enabled: this.data.enabled,
        atasanId: this.atasan.value.id,
        pejabatCutiId: this.pejabat.value.id,
        substansiId: this.substansi.value,
      };
      this.userService.update(user).subscribe((user) => {
        this.showSnackBar('User berhasil diubah');
        this.matDialogRef.close(true);
        this.isLoading = false;
      });
    }
  }

  displayName(value: any) {
    if (value) {
      return value.fullname;
    }
  }

  showSnackBar(message: string) {
    this.snackBar.open(message, 'OK', {
      duration: 3000,
    });
  }

  getEmailErrorMessage() {
    if (this.email.hasError('required')) {
      return 'Harus diisi';
    }

    return '';
  }

  getNameErrorMessage() {
    if (this.name.hasError('required')) {
      return 'Harus diisi';
    }

    return '';
  }

  getRoleErrorMessage() {
    // if (this.role.hasError("required")) {
    //   return "Harus diisi";
    // }

    return '';
  }

  getPasswordErrorMessage() {
    if (this.password.hasError('required')) {
      return 'Harus diisi';
    }
    return '';
  }

  getRetypePasswordErrorMessage() {
    if (this.retypePassword.hasError('required')) {
      return 'Harus diisi';
    }
    return '';
  }

  getErrorMessage(fc: FormControl) {
    if (fc.hasError('required')) {
      return 'Harus diisi';
    }

    return '';
  }
}
